import { MarkerType, type Edge, type Node } from "@xyflow/react";
import { ScenarioNode, ScenarioNodeType } from "types/scenario";
import { getBranchLabel, getBranchTone } from "./constants";
import { buildFlow, isMissingNode, type FlowItem } from "./utils";

export const FLOW_NODE_WIDTH = 280;
export const FLOW_ADD_WIDTH = 196;

const GAP_X = 56;
const GAP_Y = 92;
const NODE_HEIGHT = 128;
const CHOICE_HEIGHT = 196;
const ADD_HEIGHT = 48;
const MISSING_HEIGHT = 128;

const BRANCH_COLORS: Record<string, string> = {
  a: "#1769e0",
  b: "#bc6b00",
  c: "#12a36b",
  d: "#8b5cf6",
};

export type ScenarioNodeData = {
  node: ScenarioNode;
  isStart: boolean;
  branchIndex?: number;
};

export type AddBranchNodeData = {
  choiceKey: string;
  optionIndex: number;
  letter: string;
};

export type CaptionNodeData = {
  label: string;
};

export type FlowCanvasNode =
  | Node<ScenarioNodeData, "scenario">
  | Node<ScenarioNodeData, "missing">
  | Node<AddBranchNodeData, "addBranch">
  | Node<CaptionNodeData, "caption">;

export type FlowCanvasEdge = Edge;

export const getFlowNodeId = (node: ScenarioNode) =>
  isMissingNode(node) ? `missing:${node.key}` : node.key;

export const getOptionHandleId = (index: number) => `opt-${index}`;

const heightOf = (node: ScenarioNode) => {
  if (isMissingNode(node)) {
    return MISSING_HEIGHT;
  }

  if (node.type === ScenarioNodeType.Choice) {
    return CHOICE_HEIGHT;
  }

  return NODE_HEIGHT;
};

const branchColor = (branchIndex?: number) => {
  if (branchIndex == null) {
    return "#9fb0c4";
  }

  return BRANCH_COLORS[getBranchTone(branchIndex)] ?? "#9fb0c4";
};

const makeEdge = (
  source: string,
  target: string,
  sourceHandle: string,
  branchIndex?: number,
  label?: string,
): FlowCanvasEdge => {
  const color = branchColor(branchIndex);

  return {
    id: `${source}-${sourceHandle}-${target}`,
    source,
    target,
    sourceHandle,
    targetHandle: "target",
    type: "smoothstep",
    label,
    reconnectable: false,
    selectable: false,
    style: { stroke: color, strokeWidth: 1.5 },
    markerEnd: { type: MarkerType.ArrowClosed, color, width: 14, height: 14 },
    labelStyle: {
      fill: color,
      fontWeight: 800,
      fontSize: 10,
      letterSpacing: "0.06em",
    },
    labelBgStyle: { fill: "#ffffff" },
    labelBgPadding: [4, 6],
    labelBgBorderRadius: 10,
  };
};

const chainWidth = (chain: FlowItem[]): number => {
  if (!chain.length) {
    return FLOW_ADD_WIDTH;
  }

  let max = FLOW_NODE_WIDTH;

  chain.forEach((item) => {
    if (!item.branches?.length) {
      return;
    }

    const widths = item.branches.map((branch) => chainWidth(branch.items));
    const forks = widths.reduce((sum, width) => sum + width, 0) + GAP_X * Math.max(item.branches.length - 1, 0);
    max = Math.max(max, forks);
  });

  return max;
};

export const toReactFlow = (nodes: ScenarioNode[], startNodeKey: string): { nodes: FlowCanvasNode[]; edges: FlowCanvasEdge[] } => {
  const { items, orphans } = buildFlow(nodes, startNodeKey);
  const rfNodes: FlowCanvasNode[] = [];
  const rfEdges: FlowCanvasEdge[] = [];
  const placed = new Map<string, { x: number; y: number }>();
  const seenNode = new Set<string>();
  const seenEdge = new Set<string>();

  const addRfNode = (node: FlowCanvasNode) => {
    if (seenNode.has(node.id)) {
      return;
    }

    seenNode.add(node.id);
    rfNodes.push(node);
  };

  const addRfEdge = (edge: FlowCanvasEdge) => {
    if (seenEdge.has(edge.id)) {
      return;
    }

    seenEdge.add(edge.id);
    rfEdges.push(edge);
  };

  const pushScenario = (node: ScenarioNode, x: number, y: number, branchIndex: number | undefined, isStart: boolean) => {
    const id = getFlowNodeId(node);

    if (placed.has(id)) {
      return id;
    }

    placed.set(id, { x, y });
    const missing = isMissingNode(node);

    addRfNode({
      id,
      type: missing ? "missing" : "scenario",
      position: { x, y },
      draggable: false,
      connectable: false,
      selectable: !missing,
      data: { node, isStart, branchIndex },
      style: { width: FLOW_NODE_WIDTH },
    });

    return id;
  };

  const layoutChain = (
    chain: FlowItem[],
    originX: number,
    originY: number,
    branchIndex: number | undefined,
    parentId: string | null,
    parentHandle: string | null,
    isRoot: boolean,
  ): { bottomY: number; tailId: string | null } => {
    let y = originY;
    let prevId = parentId;
    let prevHandle = parentHandle;
    let tailId: string | null = parentId;

    if (!chain.length) {
      return { bottomY: y, tailId };
    }

    for (let index = 0; index < chain.length; index += 1) {
      const item = chain[index];
      const id = getFlowNodeId(item.node);
      const already = placed.has(id);

      if (!already) {
        pushScenario(item.node, originX, y, branchIndex, isRoot && index === 0 && !parentId);
      }

      if (prevId && prevHandle) {
        const forkLabel = prevHandle.startsWith("opt-") ? `NHÁNH ${getBranchLabel(branchIndex ?? 0)}` : undefined;
        addRfEdge(makeEdge(prevId, id, prevHandle, branchIndex, forkLabel));
      }

      const placedAt = placed.get(id) ?? { x: originX, y };
      tailId = id;

      if (already) {
        let last = item;

        for (let rest = index; rest < chain.length; rest += 1) {
          last = chain[rest];
          const restId = getFlowNodeId(last.node);

          if (!placed.has(restId)) {
            break;
          }

          tailId = restId;
        }

        const tailPos = tailId ? placed.get(tailId) : placedAt;
        y = (tailPos?.y ?? placedAt.y) + heightOf(last.node) + GAP_Y;
        break;
      }

      y = placedAt.y + heightOf(item.node) + GAP_Y;
      prevId = id;
      prevHandle = item.node.type === ScenarioNodeType.Choice ? null : "source";

      if (!item.branches?.length) {
        continue;
      }

      const widths = item.branches.map((branch) => Math.max(chainWidth(branch.items), FLOW_ADD_WIDTH));
      const total = widths.reduce((sum, width) => sum + width, 0) + GAP_X * Math.max(item.branches.length - 1, 0);
      let cursorX = originX + FLOW_NODE_WIDTH / 2 - total / 2;
      let maxBottom = y;

      item.branches.forEach((branch, branchCursor) => {
        const width = widths[branchCursor];
        const branchX = cursorX + width / 2 - FLOW_NODE_WIDTH / 2;
        const handle = getOptionHandleId(branch.optionIndex);
        const letter = getBranchLabel(branch.optionIndex);
        const nested = layoutChain(branch.items, branchX, y, branch.optionIndex, id, handle, false);
        const tail = branch.items.at(-1);
        const hideAdd =
          !!tail && !isMissingNode(tail.node) && tail.node.type === ScenarioNodeType.Choice;

        if (hideAdd) {
          maxBottom = Math.max(maxBottom, nested.bottomY);
          cursorX += width + GAP_X;
          return;
        }

        const addId = `add:${item.node.key}:${branch.optionIndex}`;
        const sourceId = nested.tailId ?? id;
        const sourceHandle = tail && tail.node.type !== ScenarioNodeType.Choice ? "source" : handle;
        const sourcePos = sourceId ? placed.get(sourceId) : undefined;
        const sourceHeight = tail ? heightOf(tail.node) : heightOf(item.node);
        const addY = sourcePos ? sourcePos.y + sourceHeight + GAP_Y : nested.bottomY;
        const overlap = rfNodes.filter(
          (node) => node.type === "addBranch" && Math.abs(node.position.y - addY) < 8,
        ).length;
        const addX =
          (sourcePos?.x ?? branchX) + (FLOW_NODE_WIDTH - FLOW_ADD_WIDTH) / 2 + overlap * (FLOW_ADD_WIDTH + 8);

        addRfNode({
          id: addId,
          type: "addBranch",
          position: { x: addX, y: addY },
          draggable: false,
          connectable: false,
          selectable: false,
          data: {
            choiceKey: item.node.key,
            optionIndex: branch.optionIndex,
            letter,
          },
          style: { width: FLOW_ADD_WIDTH },
        });

        addRfEdge(
          makeEdge(
            sourceId ?? id,
            addId,
            sourceHandle,
            branch.optionIndex,
            branch.items.length ? undefined : `NHÁNH ${letter}`,
          ),
        );

        maxBottom = Math.max(maxBottom, addY + ADD_HEIGHT + GAP_Y);
        cursorX += width + GAP_X;
      });

      y = maxBottom;
      prevId = null;
      prevHandle = null;
      tailId = id;
    }

    return { bottomY: y, tailId };
  };

  const root = layoutChain(items, 0, 0, undefined, null, null, true);

  if (orphans.length) {
    const bounds = [...placed.values()];
    const maxY = bounds.length ? Math.max(...bounds.map((point) => point.y), root.bottomY) : root.bottomY;
    const minX = bounds.length ? Math.min(...bounds.map((point) => point.x), 0) : 0;
    const orphanY = maxY + GAP_Y;

    addRfNode({
      id: "orphan-label",
      type: "caption",
      position: { x: minX, y: orphanY },
      draggable: false,
      connectable: false,
      selectable: false,
      data: { label: "Node chưa nối" },
    });

    orphans.forEach((node, index) => {
      pushScenario(node, minX + index * (FLOW_NODE_WIDTH + GAP_X), orphanY + 36, undefined, false);
    });
  }

  return { nodes: rfNodes, edges: rfEdges };
};
